import React, { Fragment } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dialog, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  EnvelopeIcon,
  LockClosedIcon,
  UserIcon,
  PhoneIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  ShieldCheckIcon,
} from '@heroicons/react/24/outline';
import { FaFacebookF } from 'react-icons/fa';

import * as API from '../../api';
import orderNeedsPayment from '../../shared/utils/orderNeedsPayment';

/**
 * TAF-34 — collapses the old /checkout step into the booking page.
 *
 * Opened from the booking page once the user taps Confirm. It owns the auth
 * gate + order placement that used to live on /checkout:
 *   - logged in   → a single "Confirm my booking" button → API.Order → /thankyou
 *   - logged out  → inline Sign in / Sign up, then Confirm
 *
 * `orderBooking` is the exact object the booking page used to stash in
 * localStorage('order_booking'); we still persist it (the order/thankyou
 * flow + PaymentContainer read it) and reuse the same submitOrder logic.
 *
 * Omit `orderBooking` for STANDALONE sign-in mode (the header's "Se
 * connecter"): nothing is ordered, and on success the user is sent to
 * `redirectTo` — their account by default.
 */
const ConfirmAuthSheet = ({ open, onClose, orderBooking, gift = {}, redirectTo = '/account/orders' }) => {
  const standalone = !orderBooking;
  const { t } = useTranslation();
  const history = useHistory();

  const [authStatus, setAuthStatus] = React.useState(false);
  const [auth, setAuth] = React.useState({});
  const [loadingAuthCheck, setLoadingAuthCheck] = React.useState(false);
  const [loadingOrder, setLoadingOrder] = React.useState(false);
  // One entry per social provider that the API confirms is configured. The
  // endpoint returns 503 when credentials are missing, so an unconfigured
  // provider simply never gets a button — rather than rendering one that
  // sends the user to a provider error page.
  const [socialUrls, setSocialUrls] = React.useState({});

  const [loadingLogin, setLoadingLogin] = React.useState(false);
  const [loadingRegister, setLoadingRegister] = React.useState(false);
  const [loginHelper, setLoginHelper] = React.useState({ type: '', message: '' });
  const [registerHelper, setRegisterHelper] = React.useState({ type: '', message: '' });
  const [loginEmailHelper, setLoginEmailHelper] = React.useState('');
  const [loginPasswordHelper, setLoginPasswordHelper] = React.useState('');
  const [nameHelper, setNameHelper] = React.useState('');
  const [emailHelper, setEmailHelper] = React.useState('');
  const [phoneHelper, setPhoneHelper] = React.useState('');
  const [passwordHelper, setPasswordHelper] = React.useState('');
  const [pwConfirmHelper, setPwConfirmHelper] = React.useState('');
  const [showSignup, setShowSignup] = React.useState(false);

  // Persist order_booking + run the auth check when the sheet opens.
  React.useEffect(() => {
    if (!open) return;
    // Standalone sign-in has no booking to stash.
    if (orderBooking) {
      localStorage.setItem('order_booking', JSON.stringify(orderBooking));
      localStorage.setItem('intended', '/booking');
    }

    ['google', 'facebook'].forEach((provider) => {
      API.socialLoginLink(provider)
        .then((res) => {
          if (res && res.url) {
            setSocialUrls((prev) => ({ ...prev, [provider]: res.url }));
          }
        })
        .catch(() => {
          // 503 = not configured. Leave it out so no button is rendered.
        });
    });

    // Standalone sign-in is only ever opened for a logged-OUT visitor (the
    // header redirects authenticated users straight to their account), so
    // skip the round-trip — otherwise the sheet flashes a spinner first.
    if (standalone) {
      setAuthStatus(false);
      setLoadingAuthCheck(false);
      return;
    }

    setLoadingAuthCheck(true);
    API.Auth()
      .then((res) => {
        setAuthStatus(true);
        setAuth(res.user);
      })
      .catch(() => setAuthStatus(false))
      .then(() => setLoadingAuthCheck(false));
  }, [open, orderBooking, standalone]);

  const submitLogin = (e) => {
    e.preventDefault();
    setLoadingLogin(true);
    setLoginHelper({ type: '', message: '' });
    setLoginEmailHelper('');
    setLoginPasswordHelper('');
    const fd = new FormData(e.target);
    API.SignIn({ email: fd.get('email'), password: fd.get('password') })
      .then((res) => {
        window.dispatchEvent(new Event('tafwija-auth-changed')); // refresh navbar
        // Standalone: leave straight away — see the note in submitRegister.
        if (standalone) {
          submitOrder(res.data.user);
          return;
        }
        setAuthStatus(true);
        setAuth(res.data.user);
        setLoadingLogin(false);
        // Login & confirm in one go — place the order immediately.
        submitOrder(res.data.user);
      })
      .catch((err) => {
        if (err.status === 422) {
          setLoginHelper({ type: 'error', message: err.data.message });
          if (err.data.errors.email) setLoginEmailHelper(err.data.errors.email);
          if (err.data.errors.password) setLoginPasswordHelper(err.data.errors.password);
        } else if (err.status === 401) {
          setLoginHelper({ type: 'error', message: err.data.errors });
        } else {
          setLoginHelper({ type: 'error', message: t('checkout.serverError') });
        }
        setLoadingLogin(false);
      });
  };

  const submitRegister = (e) => {
    e.preventDefault();
    setLoadingRegister(true);
    setRegisterHelper({ type: '', message: '' });
    setNameHelper('');
    setEmailHelper('');
    setPhoneHelper('');
    setPasswordHelper('');
    setPwConfirmHelper('');
    const fd = new FormData(e.target);
    const form = {
      name: fd.get('name'),
      email: fd.get('email'),
      phone: fd.get('phone'),
      password: fd.get('password'),
      password_confirmation: fd.get('password_confirmation'),
      type: 'user',
      privacy_policy: true,
    };
    if (form.password && form.password !== form.password_confirmation) {
      setPwConfirmHelper(t('checkout.passwordMismatch'));
    }
    API.SignUp(form)
      .then((res) => {
        window.dispatchEvent(new Event('tafwija-auth-changed')); // refresh navbar
        // Standalone: leave straight away. Flipping authStatus/auth first would
        // re-render the open sheet into its logged-in "confirm" panel for a
        // frame before the route change unmounts it — a visible flash.
        if (standalone) {
          submitOrder(res.data.user);
          return;
        }
        setAuthStatus(true);
        setAuth(res.data.user);
        setLoadingRegister(false);
        // Sign up & confirm in one go.
        submitOrder(res.data.user);
      })
      .catch((err) => {
        if (err.status === 422) {
          setRegisterHelper({ type: 'error', message: err.data.message });
          if (err.data.errors.name) setNameHelper(err.data.errors.name);
          if (err.data.errors.email) setEmailHelper(err.data.errors.email);
          if (err.data.errors.phone) setPhoneHelper(err.data.errors.phone);
          if (err.data.errors.password) setPasswordHelper(err.data.errors.password);
          if (err.data.errors.password_confirmation) setPwConfirmHelper(err.data.errors.password_confirmation);
        } else if (err.status === 401) {
          setRegisterHelper({ type: 'error', message: err.data.errors });
        } else {
          setRegisterHelper({ type: 'error', message: t('checkout.serverError') });
        }
        setLoadingRegister(false);
      });
  };

  // Order placement — same logic the old /checkout used. Accepts the user so
  // login/signup can place the order immediately without waiting for state.
  const submitOrder = (user = auth) => {
    // Standalone sign-in: there is no order to place — just send the user on.
    // Navigate FIRST so the route change unmounts the sheet outright; calling
    // onClose() first would play its 150ms fade-out while the new page loads,
    // which reads as the dialog flashing after signup.
    if (standalone) {
      history.push(redirectTo);
      onClose?.();
      return;
    }

    setLoadingOrder(true);
    const _event = orderBooking.event;
    let _form = {};
    if (_event.type === 'Activitie') {
      _form = {
        event_id: _event.id,
        depart_city: '1',
        date_activity: orderBooking.date_activity,
        time_activity: orderBooking.time_activity,
      };
    } else if (_event.type === 'Event') {
      _form = { event_id: _event.id, depart_city: orderBooking.city.trim() };
      if (orderBooking.roader && orderBooking.roader !== 1) Object.assign(_form, { roader: orderBooking.roader });
      else Object.assign(_form, { roader: 0 });
    } else if (_event.type === 'Sejour') {
      _form = {
        event_id: _event.id,
        depart_city: '1',
        check_in: orderBooking?.sejour_dates?.check_in ?? new Date(),
        check_out: orderBooking?.sejour_dates?.check_out ?? new Date(),
      };
    } else {
      _form = { event_id: _event.id, depart_city: '1' };
    }

    orderBooking.prices.forEach((price) => {
      if (price.qt > 0) _form[`qty_${price.id}_${_event.id}`] = parseInt(price.qt);
    });
    if (Object.keys(orderBooking.coupon).length && !parseInt(user.customer_discount)) {
      Object.assign(_form, {
        coupon_name: orderBooking.coupon.name,
        coupon_value: orderBooking.coupon.value,
      });
    }
    if (Object.keys(gift).length && gift.gift_email) Object.assign(_form, gift);

    API.Order(_form)
      .then((res) => {
        localStorage.removeItem('intended');
        localStorage.removeItem('gift');
        localStorage.removeItem('order');
        localStorage.removeItem('order_booking');
        localStorage.setItem('order_response', JSON.stringify(res.data.data));
        history.push(orderNeedsPayment(res.data.data) ? '/payment' : '/thankyou');
      })
      .catch(() => setLoadingOrder(false));
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-ink/50 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-0 sm:items-center sm:p-4">
            <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 translate-y-4 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
              <Dialog.Panel className="w-full max-w-md rounded-t-3xl bg-white shadow-elevated sm:rounded-2xl">
                {/* head */}
                <div className="relative border-b border-slate-100 px-5 py-4">
                  <Dialog.Title className="font-display text-lg font-semibold text-ink">
                    {standalone
                      ? t('nav.signIn', 'Se connecter')
                      : authStatus
                      ? t('checkout.confirm')
                      : t('checkout.signInToConfirm')}
                  </Dialog.Title>
                  {!authStatus && !loadingAuthCheck && !standalone ? (
                    <p className="mt-0.5 text-sm text-ink-muted">{t('checkout.signInToConfirmHint')}</p>
                  ) : null}
                  <button type="button" onClick={onClose} aria-label="Close" className="absolute right-3 top-4 flex h-9 w-9 items-center justify-center rounded-full text-ink-muted hover:bg-surface-muted hover:text-ink rtl:right-auto rtl:left-3">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                <div className="max-h-[70vh] overflow-y-auto px-5 py-5">
                  {loadingAuthCheck ? (
                    <div className="flex h-32 items-center justify-center">
                      <Spinner tone="brand" />
                    </div>
                  ) : authStatus ? (
                    // ---- Logged in: order summary mini + confirm ----
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 rounded-xl border border-emerald-100 bg-emerald-50 p-3">
                        <CheckCircleIcon className="h-5 w-5 shrink-0 text-emerald-600" />
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-ink">{auth.name}</p>
                          <p className="truncate text-xs text-ink-muted">{auth.email}</p>
                        </div>
                      </div>
                      {!standalone ? (
                        <div className="flex items-center justify-between rounded-xl bg-surface-muted px-4 py-3">
                          <span className="text-sm text-ink-muted">{orderBooking?.event?.title}</span>
                          <bdi className="font-display text-base font-bold text-brand-500">
                            {API.priceFormat(orderBooking?.price_coupon?.total ?? orderBooking?.price_total ?? 0)}
                          </bdi>
                        </div>
                      ) : null}
                      <button
                        type="button"
                        onClick={submitOrder}
                        disabled={loadingOrder}
                        className="flex w-full items-center justify-center gap-2 rounded-xl bg-accent-500 px-4 py-3.5 text-sm font-bold text-brand-900 shadow-card transition-colors hover:bg-accent-400 disabled:opacity-60"
                      >
                        {loadingOrder ? <Spinner tone="dark" /> : (
                          <>
                            <ShieldCheckIcon className="h-5 w-5" />
                            {standalone ? t('nav.myAccount', 'Mon compte') : t('checkout.confirm')}
                          </>
                        )}
                      </button>
                      {!standalone ? (
                        <p className="text-center text-xs leading-relaxed text-ink-subtle">{t('checkout.processingNote')}</p>
                      ) : null}
                    </div>
                  ) : (
                    // ---- Logged out: social + sign in (sign up toggle) ----
                    <div className="space-y-4">
                      {socialUrls.google ? (
                        <a
                          href={socialUrls.google}
                          className="flex w-full items-center justify-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-ink transition-colors hover:bg-surface-subtle"
                        >
                          {/* Google's mark must stay multi-colour per their brand
                              rules, so it is inlined rather than tinted. */}
                          <svg className="h-4 w-4" viewBox="0 0 18 18" aria-hidden="true">
                            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z" />
                            <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z" />
                            <path fill="#FBBC05" d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.96H.96a9 9 0 0 0 0 8.08l3.01-2.32Z" />
                            <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l3.01 2.32C4.68 5.16 6.66 3.58 9 3.58Z" />
                          </svg>
                          {t('checkout.withGoogle', 'Continuer avec Google')}
                        </a>
                      ) : null}
                      {socialUrls.facebook ? (
                        <a href={socialUrls.facebook} className="flex w-full items-center justify-center gap-2.5 rounded-xl bg-[#1877F2] px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90">
                          <FaFacebookF className="h-4 w-4" />
                          {t('checkout.withFacebook')}
                        </a>
                      ) : null}
                      {/* "or" only separates something from something. With no
                          configured provider above it, it would dangle. */}
                      {(socialUrls.google || socialUrls.facebook) ? (
                        <div className="flex items-center gap-3 text-xs uppercase tracking-wide text-ink-subtle">
                          <span className="h-px flex-1 bg-slate-200" />{t('checkout.or')}<span className="h-px flex-1 bg-slate-200" />
                        </div>
                      ) : null}

                      {!showSignup ? (
                        <form onSubmit={submitLogin} className="space-y-3">
                          {loginHelper.message ? <Alert type={loginHelper.type} html={loginHelper.message} /> : null}
                          <TextInput name="email" type="email" placeholder={t('checkout.email')} icon={EnvelopeIcon} error={loginEmailHelper} disabled={loadingLogin} />
                          <TextInput name="password" type="password" placeholder={t('checkout.password')} icon={LockClosedIcon} error={loginPasswordHelper} disabled={loadingLogin} />
                          <div className="text-right rtl:text-left">
                            {/* Close the sheet on the way out — a bare <Link>
                                changes the route but leaves the dialog mounted
                                on top of the reset page. */}
                            <Link to="/reset" onClick={onClose} className="text-xs font-medium text-brand-500 hover:underline">{t('checkout.forgotPassword')}</Link>
                          </div>
                          <PrimaryBtn loading={loadingLogin} label={standalone ? t('nav.signIn', 'Se connecter') : t('checkout.signInConfirm')} />
                          <p className="text-center text-sm text-ink-muted">
                            <button type="button" onClick={() => setShowSignup(true)} className="font-semibold text-brand-500 hover:underline">
                              {t('checkout.signupTitle')}
                            </button>
                          </p>
                        </form>
                      ) : (
                        <form onSubmit={submitRegister} className="space-y-3">
                          {registerHelper.message ? <Alert type={registerHelper.type} html={registerHelper.message} /> : null}
                          <TextInput name="name" placeholder={t('checkout.name')} icon={UserIcon} error={nameHelper} disabled={loadingRegister} />
                          <TextInput name="email" type="email" placeholder={t('checkout.email')} icon={EnvelopeIcon} error={emailHelper} disabled={loadingRegister} />
                          <TextInput name="phone" type="tel" placeholder={t('checkout.phone')} icon={PhoneIcon} error={phoneHelper} disabled={loadingRegister} />
                          <TextInput name="password" type="password" placeholder={t('checkout.password')} icon={LockClosedIcon} error={passwordHelper} disabled={loadingRegister} />
                          <TextInput name="password_confirmation" type="password" placeholder={t('checkout.passwordConfirm')} icon={LockClosedIcon} error={pwConfirmHelper} disabled={loadingRegister} />
                          <PrimaryBtn loading={loadingRegister} label={standalone ? t('nav.signUp', 'Créer un compte') : t('checkout.signUpConfirm')} />
                          <p className="text-center text-sm text-ink-muted">
                            <button type="button" onClick={() => setShowSignup(false)} className="font-semibold text-brand-500 hover:underline">
                              {t('checkout.loginTitle')}
                            </button>
                          </p>
                        </form>
                      )}
                    </div>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

// ---- helpers ----------------------------------------------------------------

function Spinner({ tone = 'light' }) {
  const ring = tone === 'brand' ? 'border-brand-500/30 border-t-brand-500' : tone === 'dark' ? 'border-brand-900/30 border-t-brand-900' : 'border-white/40 border-t-white';
  return <span className={`inline-block h-5 w-5 animate-spin rounded-full border-2 ${ring}`} />;
}

function TextInput({ name, type = 'text', placeholder, icon: Icon, error, disabled }) {
  return (
    <div>
      <div className="relative">
        <input name={name} type={type} placeholder={placeholder} disabled={disabled} className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-3.5 pr-10 text-sm text-ink placeholder:text-ink-subtle focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100 disabled:bg-slate-50 rtl:pl-10 rtl:pr-3.5" />
        {Icon ? <Icon className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-subtle rtl:right-auto rtl:left-3" /> : null}
      </div>
      {error ? <p className="mt-1 text-xs text-rose-500">{error}</p> : null}
    </div>
  );
}

function PrimaryBtn({ loading, label }) {
  return (
    <button type="submit" disabled={loading} className="flex w-full items-center justify-center rounded-xl bg-brand-500 px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-600 disabled:opacity-60">
      {loading ? <Spinner /> : label}
    </button>
  );
}

function Alert({ type, html }) {
  const ok = type === 'success';
  const Icon = ok ? CheckCircleIcon : ExclamationCircleIcon;
  return (
    <div className={`flex items-start gap-2 rounded-xl border px-3.5 py-2.5 text-sm ${ok ? 'border-emerald-100 bg-emerald-50 text-emerald-700' : 'border-rose-100 bg-rose-50 text-rose-600'}`}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" />
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </div>
  );
}

export default ConfirmAuthSheet;
